import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { enquiriesAPI } from '../../utils/api';
import '../../styles/AdminTable.scss';

const AdminEnquiries = ({ type }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchParamVal = searchParams.get('search') || '';
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState(searchParamVal);

  useEffect(() => {
    fetchEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    setSearchQuery(searchParams.get('search') || '');
  }, [searchParams]);

  useEffect(() => {
    filterEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiries, statusFilter, searchQuery, type]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      setError('');
      // Fetch utilizing the specific CRM endpoints
      const response = type === 'mba'
        ? await enquiriesAPI.getMbaEnquiries()
        : await enquiriesAPI.getSoftwareEnquiries();

      const responseData = response.data || response;
      const enquiriesArray = Array.isArray(responseData.data) ? responseData.data : [];
      setEnquiries(enquiriesArray);
    } catch (err) {
      setError('Failed to load enquiries');
      console.error('Error fetching enquiries:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEnquiry = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this enquiry? This action cannot be undone.')) {
      try {
        setLoading(true);
        setError('');
        await enquiriesAPI.delete(id);
        setEnquiries(prev => prev.filter(item => item.id !== id));
      } catch (err) {
        setError('Failed to delete enquiry');
        console.error('Error deleting enquiry:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const filterEnquiries = () => {
    let result = [...enquiries];

    if (statusFilter !== 'All') {
      result = result.filter(e => e.status === statusFilter);
    }

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        e =>
          (e.name && e.name.toLowerCase().includes(query)) ||
          (e.email && e.email.toLowerCase().includes(query)) ||
          (e.mobile && e.mobile.includes(query)) ||
          (e.projectTitle && e.projectTitle.toLowerCase().includes(query)) ||
          (e.specialization && e.specialization.toLowerCase().includes(query)) ||
          (e.status && e.status.toLowerCase().includes(query))
      );
    }

    setFilteredEnquiries(result);
  };

  const exportCsv = () => {
    if (!filteredEnquiries || filteredEnquiries.length === 0) return;

    const headers = [
      'Date',
      'Name',
      'Email',
      'Mobile',
      'Project Title',
      'Specialization',
      'Status',
      'Created Date',
      'Completed Date'
    ];

    const rows = filteredEnquiries.map(r => [
      new Date(r.createdAt || r.created_at).toLocaleDateString(),
      `"${(r.name ?? '').replace(/"/g, '""')}"`,
      `"${(r.email ?? '').replace(/"/g, '""')}"`,
      `"${(r.mobile ?? '').replace(/"/g, '""')}"`,
      `"${(r.projectTitle ?? '').replace(/"/g, '""')}"`,
      `"${formatSpecialization(r.specialization).replace(/"/g, '""')}"`,
      `"${(r.status ?? '').replace(/"/g, '""')}"`,
      new Date(r.createdAt || r.created_at).toLocaleString(),
      r.completedAt ? new Date(r.completedAt).toLocaleString() : 'N/A'
    ].join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_project_enquiries_${statusFilter}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    const colors = {
      New: '#FFA500',        // Orange
      Open: '#4318ff',       // Primary Blue
      Pending: '#4169E1',    // Royal Blue
      Completed: '#01b574',  // Green
    };
    return colors[status] || '#808080';
  };

  const formatSpecialization = (spec) => {
    if (!spec) return '';
    return spec.replace(/^(MBA - |Software - )/, '');
  };

  // Stats
  const stats = {
    total: enquiries.length,
    new: enquiries.filter(e => e.status === 'New').length,
    open: enquiries.filter(e => e.status === 'Open').length,
    pending: enquiries.filter(e => e.status === 'Pending').length,
    completed: enquiries.filter(e => e.status === 'Completed').length,
  };

  return (
    <div className="admin-page admin-table-styles">
      <div className="page-header">
        <h1>{type === 'mba' ? 'MBA Project Enquiries' : 'Software Project Enquiries'}</h1>
        <p>Manage lead information and workflow statuses for {type === 'mba' ? 'MBA' : 'Software'} Project requests</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Total Enquiries</h3>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="stat-card orange">
          <div className="stat-icon">🆕</div>
          <div className="stat-content">
            <h3>New Leads</h3>
            <p className="stat-value">{stats.new}</p>
          </div>
        </div>
        <div className="stat-card blue" style={{ borderLeftColor: '#4318ff' }}>
          <div className="stat-icon" style={{ background: 'rgba(67, 24, 255, 0.1)', color: '#4318ff' }}>📂</div>
          <div className="stat-content">
            <h3>Open</h3>
            <p className="stat-value">{stats.open}</p>
          </div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon">✔️</div>
          <div className="stat-content">
            <h3>Completed</h3>
            <p className="stat-value">{stats.completed}</p>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="admin-actions-bar" style={{ display: 'flex', justifyContent: 'space-between', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="filters-group" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
          >
            <option value="All">All Statuses</option>
            <option value="New">New</option>
            <option value="Open">Open</option>
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
          </select>

          <input
            type="text"
            placeholder="Search name, email, mobile, project..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', width: '280px', outline: 'none' }}
          />
        </div>

        <div className="buttons-group" style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn-primary"
            onClick={fetchEnquiries}
            disabled={loading}
            style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {loading ? 'Refreshing...' : '🔄 Refresh'}
          </button>
          <button
            className="btn-primary"
            onClick={exportCsv}
            disabled={filteredEnquiries.length === 0}
            style={{ padding: '8px 16px', background: '#4a5568' }}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="content-wrapper">
        <div className="table-container" style={{ width: '100%' }}>
          {loading && enquiries.length === 0 ? (
            <p style={{ padding: '20px' }}>Loading enquiries...</p>
          ) : filteredEnquiries.length === 0 ? (
            <p style={{ padding: '20px' }}>No matching enquiries found</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Project Title</th>
                  <th>Specialization</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnquiries.map((enquiry) => (
                  <tr key={enquiry.id} className={enquiry.status === 'New' ? 'unread' : ''}>
                    <td>{new Date(enquiry.createdAt || enquiry.created_at).toLocaleDateString()}</td>
                    <td style={{ fontWeight: 600 }}>{enquiry.name}</td>
                    <td>
                      <a href={`mailto:${enquiry.email}`}>{enquiry.email}</a>
                    </td>
                    <td>
                      <a href={`tel:${enquiry.mobile}`}>{enquiry.mobile}</a>
                    </td>
                    <td style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={enquiry.projectTitle}>
                      {enquiry.projectTitle}
                    </td>
                    <td>{formatSpecialization(enquiry.specialization)}</td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: getStatusColor(enquiry.status),
                          color: 'white'
                        }}
                      >
                        {enquiry.status}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-view"
                        onClick={() => navigate(`/admin/${type}-enquiries/${enquiry.id}`)}
                        title="View Details"
                      >
                        View
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteEnquiry(enquiry.id)}
                        title="Delete Enquiry"
                        style={{
                          background: '#fff5f5',
                          color: '#e53e3e',
                          border: '1px solid #fed7d7',
                          padding: '8px 14px',
                          borderRadius: '20px',
                          fontWeight: '700',
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(229, 62, 62, 0.05)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#e53e3e';
                          e.target.style.color = 'white';
                          e.target.style.borderColor = '#e53e3e';
                          e.target.style.boxShadow = '0 4px 10px rgba(229, 62, 62, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#fff5f5';
                          e.target.style.color = '#e53e3e';
                          e.target.style.borderColor = '#fed7d7';
                          e.target.style.boxShadow = '0 2px 4px rgba(229, 62, 62, 0.05)';
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEnquiries;
